// TelcoTrack - Role Constants
// Optimized Czech role definitions for ProTelco system

// Base role names
export const ROLES = {
  /** Nadřazená role: polygony (vytvoření/mazání), správa uživatelů, Import DB — výhradně tato role pro tyto akce */
  SUPERADMIN: 'superadmin',
  /**
   * Správce firmy (subdodavatel) — zve a spravuje uživatele své firmy
   * a nastavuje jejich sazby v rámci budgetu, který určil superadmin.
   * Neotevírá Finance, Fakturaci ani cizí výkazy mimo vlastní firmu.
   * Role key zůstává `org_admin` (API / JWT); uživatelský popisek je Firma.
   */
  ORG_ADMIN: 'org_admin',
  ADMIN: 'admin',
  /**
   * Finance — dohled nad financemi / cizími výkazy / Fakturací.
   * Bez mazání polygonů, správy uživatelů a Import menu.
   */
  FINANCE: 'finance',
  /**
   * Import — menu Import, import dat, zakládání objektů na mapě,
   * mazání objektů (ne polygonů). Bez financí / správy uživatelů / mazání polygonů.
   */
  IMPORT: 'import',
  // Role podle typu práce (kombinovatelné)
  ZAFUK: 'zafuk',
  MONTAZ: 'montaz',
  STAVBA: 'stavba',
  OPTIKA: 'optika',
  /**
   * Subdodavatel — pouze čtení (projekty, výkresy vč. obecného, objekty).
   * Bez editace a bez výkazu práce / sazeb / financí.
   */
  VIEWER: 'viewer',
  /**
   * Subdodavatel — jako viewer + editace objektů (fáze, obsah).
   * Bez výkazu práce / sazeb / financí (aby neviděli odměny).
   */
  EDITOR: 'editor',
  /**
   * Sklad — plná editace skladu (příjem/výdej/sklady) a materiálu v menu Materiál.
   * Bez správy uživatelů / polygon importu (není finance/admin).
   */
  SKLAD: 'sklad',
  /**
   * Zařízení — registr přístrojů, rezervace, servisy, převody (menu `/equipment`).
   * Oddělené od role Sklad — sklad bez `zarizeni` menu Zařízení nevidí.
   */
  ZARIZENI: 'zarizeni',
  /**
   * Akvizice — jako viewer (read-only mapa/objekty) + výkres Akvizice
   * a generace / podpis / archivace smluv (souhlas, digitální cesta, parties, přílohy).
   * Podpis smí i mutátoři obsahu; výkres vidí jen tato role.
   */
  AKVIZICE: 'akvizice',
  /**
   * Dokumentarista — jako viewer + menu „Změny oproti PD“ (workflow poznámky,
   * status Vyřešeno / Ignorovat). Stejná read-only baseline jako viewer jinak.
   */
  DOKUMENTARISTA: 'dokumentarista',
  /**
   * Příprava staveb — křížení sítí, povinnosti; e-mail skládá z workflow
   * (schránka Gmail je v hubu Komunikace).
   * Desk role; bez mutace mapových objektů.
   */
  PRIPRAVA_STAVEB: 'priprava_staveb',
  /**
   * Inženýring — dotčené orgány, věcná břemena.
   * Desk role; bez mutace mapových objektů. Role key remains `bremena`.
   * Datovku (schránku ISDS) spravuje samostatná role {@link ROLES.DATOVKA}.
   */
  BREMENA: 'bremena',
  /**
   * Datovka — vidí a odesílá ze schránky ISDS (Komunikace → Datovka),
   * schvaluje odchozí datové zprávy z workflow (Ke schválení).
   * Desk role; bez mutace mapových objektů.
   */
  DATOVKA: 'datovka',
  /**
   * Personalistka — generace HR smluv nástupu (nástupy / Google Drive).
   * Desk role; bez mutace mapových objektů.
   */
  PERSONALISTKA: 'personalistka',
  /**
   * Navolávání — menu Navolávání a správa Google kalendářů v Nastavení
   * (vytvořit / přejmenovat / smazat + zapnutí v katalogu). Stav budov nemění.
   * Desk role; bez mutace mapových objektů.
   */
  NAVOLAVANI: 'navolavani',
} as const;

export type RoleId = (typeof ROLES)[keyof typeof ROLES];

/**
 * Removed desk role — migrated to {@link ROLES.PRIPRAVA_STAVEB} + {@link ROLES.BREMENA}.
 * Not assignable; {@link expandLegacyRoles} / {@link normalizeAssignableRoles} rewrite it.
 */
export const LEGACY_ROLE_URADY = 'urady' as const;

/**
 * Removed office role — migrated to {@link ROLES.FINANCE}.
 * Not assignable; {@link expandLegacyRoles} / {@link normalizeAssignableRoles} rewrite it.
 */
export const LEGACY_ROLE_MANAGER = 'manager' as const;

/** Všechny přiřaditelné role (pozvánky / správa uživatelů / DTO validace). */
export const ASSIGNABLE_ROLES: readonly RoleId[] = [
  ROLES.SUPERADMIN,
  ROLES.ORG_ADMIN,
  ROLES.ADMIN,
  ROLES.FINANCE,
  ROLES.IMPORT,
  ROLES.SKLAD,
  ROLES.ZARIZENI,
  ROLES.STAVBA,
  ROLES.MONTAZ,
  ROLES.ZAFUK,
  ROLES.OPTIKA,
  ROLES.VIEWER,
  ROLES.EDITOR,
  ROLES.AKVIZICE,
  ROLES.DOKUMENTARISTA,
  ROLES.PRIPRAVA_STAVEB,
  ROLES.BREMENA,
  ROLES.DATOVKA,
  ROLES.PERSONALISTKA,
  ROLES.NAVOLAVANI,
] as const;

/** České popisky rolí pro UI. */
export const ROLE_LABELS_CS: Record<RoleId, string> = {
  [ROLES.SUPERADMIN]: 'Superadministrátor',
  [ROLES.ORG_ADMIN]: 'Správce firmy',
  [ROLES.ADMIN]: 'Administrátor',
  [ROLES.FINANCE]: 'Finance',
  [ROLES.IMPORT]: 'Import',
  [ROLES.SKLAD]: 'Sklad',
  [ROLES.ZARIZENI]: 'Zařízení',
  [ROLES.STAVBA]: 'Stavba',
  [ROLES.MONTAZ]: 'Montáž',
  [ROLES.ZAFUK]: 'Zafuk',
  [ROLES.OPTIKA]: 'Optika',
  [ROLES.VIEWER]: 'Prohlížeč',
  [ROLES.EDITOR]: 'Editor',
  [ROLES.AKVIZICE]: 'Akvizice',
  [ROLES.DOKUMENTARISTA]: 'Dokumentarista',
  [ROLES.PRIPRAVA_STAVEB]: 'Příprava staveb',
  [ROLES.BREMENA]: 'Inženýring',
  [ROLES.DATOVKA]: 'Datovka',
  [ROLES.PERSONALISTKA]: 'Personalistka',
  [ROLES.NAVOLAVANI]: 'Navolávání',
};

/** Položky pro checkboxy v pozvánkách / editaci uživatele. */
export const ASSIGNABLE_ROLE_OPTIONS: readonly { id: RoleId; label: string }[] =
  ASSIGNABLE_ROLES.map((id) => ({ id, label: ROLE_LABELS_CS[id] }));

export function formatRoleLabelsCs(roles: string[] | undefined | null): string {
  if (!roles?.length) return '';
  return expandLegacyRoles(roles)
    .map((role) => {
      const key = role as RoleId;
      return ROLE_LABELS_CS[key] || role;
    })
    .join(', ');
}

/**
 * Rewrite removed roles before access checks / display.
 * - `urady` → `priprava_staveb` + `bremena` (same cutover as DB migration).
 * - `manager` → `finance` (office split: Finance / Import / Superadmin).
 */
export function expandLegacyRoles(roles: string[] | undefined | null): string[] {
  if (!roles?.length) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  let hadUrady = false;
  let hadManager = false;
  for (const raw of roles) {
    const r = (raw || '').toLowerCase().trim();
    if (!r) continue;
    if (r === LEGACY_ROLE_URADY) {
      hadUrady = true;
      continue;
    }
    if (r === LEGACY_ROLE_MANAGER) {
      hadManager = true;
      continue;
    }
    if (seen.has(r)) continue;
    seen.add(r);
    out.push(r);
  }
  if (hadUrady) {
    for (const r of [ROLES.PRIPRAVA_STAVEB, ROLES.BREMENA]) {
      if (seen.has(r)) continue;
      seen.add(r);
      out.push(r);
    }
  }
  if (hadManager) {
    if (!seen.has(ROLES.FINANCE)) {
      seen.add(ROLES.FINANCE);
      out.push(ROLES.FINANCE);
    }
  }
  return out;
}

/**
 * Roles safe to persist / send in PATCH (assignable only).
 * Expands legacy `urady` / `manager`, drops unknown keys so invalid roles can be cleared.
 */
export function normalizeAssignableRoles(roles: string[] | undefined | null): string[] {
  const allowed = new Set<string>(ASSIGNABLE_ROLES);
  return expandLegacyRoles(roles).filter((r) => allowed.has(r));
}

export function isAssignableRole(role: string | null | undefined): boolean {
  const key = (role || '').toLowerCase().trim();
  return (ASSIGNABLE_ROLES as readonly string[]).includes(key);
}

/** Role, které smí superadmin povolit správci firmy. `superadmin` a `org_admin` nikdy. */
const COMPANY_GRANTABLE_ROLES = new Set<string>(
  ASSIGNABLE_ROLES.filter((r) => r !== ROLES.SUPERADMIN && r !== ROLES.ORG_ADMIN),
);

export function hasOrgAdminRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.ORG_ADMIN);
}

/** Seznam rolí, které smí org admin přiřadit — po validaci a bez superadmin / org_admin. */
export function sanitizeCompanyRoleGrants(roles: string[] | undefined | null): string[] {
  return normalizeAssignableRoles(roles).filter((r) => COMPANY_GRANTABLE_ROLES.has(r));
}

/** Role z editace uživatele oříznuté na grant firmy. */
export function filterRolesToCompanyGrants(
  roles: string[] | undefined | null,
  grants: string[] | undefined | null,
): string[] {
  const allowed = new Set(sanitizeCompanyRoleGrants(grants));
  return normalizeAssignableRoles(roles).filter((r) => allowed.has(r));
}

function normalizeRoles(roles: string[] | undefined | null): string[] {
  return expandLegacyRoles(roles);
}

/** API / gateway: role superadmin (case-insensitive match on array elements). */
export function hasSuperAdminRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.SUPERADMIN);
}

/** Admin nebo superadmin — např. úprava polygonového ceníku výkazu práce. */
export function hasAdminOrSuperAdminRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((x) => x === ROLES.ADMIN || x === ROLES.SUPERADMIN);
}

export function hasFinanceRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.FINANCE);
}

export function hasImportRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.IMPORT);
}

/**
 * Finance / admin / superadmin — Finance dashboard, cizí výkazy, Fakturace, billing dohled.
 */
export function hasFinanceAdminOrSuperAdminRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some(
    (x) => x === ROLES.FINANCE || x === ROLES.ADMIN || x === ROLES.SUPERADMIN,
  );
}

/**
 * Import menu + import API — role `import` nebo superadmin.
 * (Admin bez import role Import menu nevidí.)
 */
export function hasImportOrSuperAdminRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((x) => x === ROLES.IMPORT || x === ROLES.SUPERADMIN);
}

/**
 * Import / admin / superadmin — akviziční KN mutace, force-delete objektů (ne polygonů),
 * dříve „manažer+“ u import/editace akvizice.
 */
export function hasImportAdminOrSuperAdminRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some(
    (x) => x === ROLES.IMPORT || x === ROLES.ADMIN || x === ROLES.SUPERADMIN,
  );
}

/**
 * Mazání mapových objektů (force / libovolný typ) — import / admin / superadmin.
 * Mazání polygonů zůstává výhradně superadmin.
 */
export function canDeleteMapObjects(roles: string[] | undefined | null): boolean {
  return hasImportAdminOrSuperAdminRole(roles);
}

/**
 * Admin, superadmin nebo finance — zobrazení a úprava specifikace polygonu (metadata, obchodní položky)
 * a odpovídající `PUT /polygons/:id` + `PATCH …/commercial-items` v polygon-service.
 */
export function hasPolygonSpecificationEditorRole(roles: string[] | undefined | null): boolean {
  return hasFinanceAdminOrSuperAdminRole(roles);
}

/** Stavba nebo nadřízené role — stavební deník (API object-service + web/Android UI). */
export function hasConstructionDiaryAccessRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some(
    (x) => x === ROLES.STAVBA || x === ROLES.ADMIN || x === ROLES.SUPERADMIN,
  );
}

const FIELD_ROLES_STAVBA_MONTAZ_ZAFUK_OPTIKA = new Set<string>([
  ROLES.STAVBA,
  ROLES.MONTAZ,
  ROLES.ZAFUK,
  ROLES.OPTIKA,
]);

const SUBCONTRACTOR_ROLES = new Set<string>([ROLES.VIEWER, ROLES.EDITOR]);

/** Role s viewer-level read-only baseline (bez mutace objektů mimo svůj specializovaný modul). */
const VIEWER_LIKE_ROLES = new Set<string>([
  ROLES.VIEWER,
  ROLES.AKVIZICE,
  ROLES.DOKUMENTARISTA,
]);

/** Terénní role (stavba / montáž / záfuk / optika). */
export function hasFieldWorkRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((r) => FIELD_ROLES_STAVBA_MONTAZ_ZAFUK_OPTIKA.has(r));
}

export function hasViewerRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.VIEWER);
}

export function hasEditorRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.EDITOR);
}

export function hasAkviziceRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.AKVIZICE);
}

export function hasDokumentaristaRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.DOKUMENTARISTA);
}

export function hasPripravaStavebRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.PRIPRAVA_STAVEB);
}

export function hasBremenaRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.BREMENA);
}

export function hasDatovkaRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.DATOVKA);
}

export function hasPersonalistkaRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.PERSONALISTKA);
}

export function hasNavolavaniRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.NAVOLAVANI);
}

export function hasSkladRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.SKLAD);
}

export function hasZarizeniRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).includes(ROLES.ZARIZENI);
}

/**
 * Menu / route **Zařízení** a čtení `/api/objects/equipment/*` (rezervace, převody).
 * Role `zarizeni` nebo administrátor / superadministrátor.
 */
export function hasEquipmentAccessRole(roles: string[] | undefined | null): boolean {
  return hasZarizeniRole(roles) || hasAdminOrSuperAdminRole(roles);
}

/**
 * Registr zařízení, servisy, vyřazení — stejná množina jako {@link hasEquipmentAccessRole}.
 */
export function hasEquipmentEditRole(roles: string[] | undefined | null): boolean {
  return hasEquipmentAccessRole(roles);
}

/**
 * Menu / API **Změny oproti PD** — pouze role `dokumentarista` (bez auto-přístupu admin/superadmin).
 */
export function canAccessPdChangesNotes(roles: string[] | undefined | null): boolean {
  return hasDokumentaristaRole(roles);
}

/**
 * Generace HR smluv nástupu (nástupy) — pouze role `personalistka`.
 * Interní volání auth → object-service používá service token (mimo tuto kontrolu).
 */
export function canGenerateHrOnboardingContracts(roles: string[] | undefined | null): boolean {
  return hasPersonalistkaRole(roles);
}

/** Mzda v HR profilu — personalistka a superadmin. */
export function canViewUserHrSalary(roles: string[] | undefined | null): boolean {
  return hasPersonalistkaRole(roles) || hasSuperAdminRole(roles);
}

/**
 * Finance / admin / superadmin — nadřízené office role pro adresář uživatelů a cizí výkazy.
 * (Nahrazuje dřívější manažer / admin / superadmin.)
 */
export function hasFinanceOversightRole(roles: string[] | undefined | null): boolean {
  return hasFinanceAdminOrSuperAdminRole(roles);
}

/**
 * Editace skladu (příjem zásob, CRUD skladů) — pouze role `sklad`
 * (bez auto-přístupu admin/superadmin). Stejná množina jako {@link hasWarehouseIssueRole}.
 */
export function hasWarehouseEditRole(roles: string[] | undefined | null): boolean {
  return hasWarehouseIssueRole(roles);
}

/**
 * Výdej ze skladu (stock/remove, scan/take, GoodsIssue z poboček) —
 * **pouze** role `sklad` (bez auto-přístupu admin/superadmin). Terénní role výdej
 * neprovádí (osobní materiál = hub + převody; spotřeba = material_usage auto-výdej).
 */
export function hasWarehouseIssueRole(roles: string[] | undefined | null): boolean {
  return hasSkladRole(roles);
}

/** Office / platform roles that are not „jen terén“. */
function hasNonFieldOfficeRole(roles: string[] | undefined | null): boolean {
  return (
    hasFinanceAdminOrSuperAdminRole(roles) ||
    hasImportRole(roles)
  );
}

/**
 * Uživatel má **jen** terénní role (stavba, montáž, záfuk, optika) bez finance/admin/import —
 * nesmí číst agregovaný seznam uživatelů, historii ani výkaz **jiného** uživatele (API + web).
 */
export function isFieldWorkOnlyNoManagementRole(roles: string[] | undefined | null): boolean {
  const normalized = normalizeRoles(roles);
  if (!normalized.length) return false;
  if (hasNonFieldOfficeRole(normalized)) return false;
  return normalized.every((r) => FIELD_ROLES_STAVBA_MONTAZ_ZAFUK_OPTIKA.has(r));
}

/**
 * Subdodavatelské / viewer-like role (viewer/editor/akvizice) bez finance/admin/import a bez jiné
 * „interní“ role — včetně kombinace jen s terénními rolemi: nesmí adresář uživatelů / cizí výkazy / finance.
 */
export function isSubcontractorLimitedRole(roles: string[] | undefined | null): boolean {
  const normalized = normalizeRoles(roles);
  if (!normalized.length) return false;
  if (hasNonFieldOfficeRole(normalized)) return false;
  const limitedBase = new Set<string>([...SUBCONTRACTOR_ROLES, ...VIEWER_LIKE_ROLES]);
  const restricted = new Set<string>([
    ...FIELD_ROLES_STAVBA_MONTAZ_ZAFUK_OPTIKA,
    ...limitedBase,
  ]);
  if (!normalized.some((r) => limitedBase.has(r))) return false;
  return normalized.every((r) => restricted.has(r));
}

/**
 * Opak {@link isFieldWorkOnlyNoManagementRole} / subdodavatelského omezení —
 * smí volat GET …/users/activity, …/:id/activity, …/work-report/user/:id, Finance.
 * Čistý `import` (bez finance/admin a bez jiné interní role) → ne.
 */
export function canViewOtherUsersActivityAndWorkReport(roles: string[] | undefined | null): boolean {
  // `org_admin` sám o sobě Finance / cizí výkazy neotevírá (ani v kombinaci s terénní rolí).
  const normalized = normalizeRoles(roles).filter((r) => r !== ROLES.ORG_ADMIN);
  if (!normalized.length) return false;
  if (hasFinanceAdminOrSuperAdminRole(normalized)) return true;
  if (isFieldWorkOnlyNoManagementRole(normalized)) return false;
  if (isSubcontractorLimitedRole(normalized)) return false;
  if (normalized.every((r) => SUBCONTRACTOR_ROLES.has(r) || VIEWER_LIKE_ROLES.has(r))) return false;
  // Pure import (optionally + viewer-like) must not unlock Finance / cizí výkazy
  const elevated = normalized.filter(
    (r) => r !== ROLES.IMPORT && !SUBCONTRACTOR_ROLES.has(r) && !VIEWER_LIKE_ROLES.has(r),
  );
  if (!elevated.length) return false;
  return true;
}

/**
 * Menu / route **Fakturace** — finance / admin / superadmin.
 */
export function canAccessFakturace(roles: string[] | undefined | null): boolean {
  return hasFinanceAdminOrSuperAdminRole(roles);
}

/**
 * Menu / route **Import** — import / superadmin.
 */
export function canAccessImportMenu(roles: string[] | undefined | null): boolean {
  return hasImportOrSuperAdminRole(roles);
}

/**
 * Menu / route **Výkaz práce** (`/work-report`) — stejné oprávnění jako API
 * {@link canAccessWorkReportAndRates} (terénní role nebo finance/admin/superadmin).
 */
export function canAccessWorkReportMenu(roles: string[] | undefined | null): boolean {
  return canAccessWorkReportAndRates(roles);
}

/**
 * Výdělek firmy (smluvní tržba vs. odměny). Správce firmy ho vidí i bez záložky Firmy.
 * Neotevírá správu firem, pozvánky ani ceník.
 */
export function canReadCompanyEarnings(roles: string[] | undefined | null): boolean {
  return hasOrgAdminRole(roles) || canViewOtherUsersActivityAndWorkReport(roles);
}

/**
 * Výkaz práce, sazby, polygonový výkaz / finance s cenami —
 * terénní role **nebo** finance/admin/superadmin.
 * **viewer** a **editor** (bez těchto) nemají přístup.
 */
export function canAccessWorkReportAndRates(roles: string[] | undefined | null): boolean {
  if (!roles?.length) return false;
  if (hasFinanceAdminOrSuperAdminRole(roles)) return true;
  return hasFieldWorkRole(roles);
}

/**
 * Mutace obsahu objektu na mapě (fotky, materiál, poznámky, fáze, vytvoření bodů).
 * **viewer** bez jiné mutační role = read-only.
 */
const OBJECT_CONTENT_MUTATOR_ROLES = new Set<string>([
  ROLES.SUPERADMIN,
  ROLES.ADMIN,
  ROLES.IMPORT,
  ROLES.SKLAD,
  ROLES.STAVBA,
  ROLES.MONTAZ,
  ROLES.ZAFUK,
  ROLES.OPTIKA,
  ROLES.EDITOR,
]);

export function hasObjectContentMutatorRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((r) => OBJECT_CONTENT_MUTATOR_ROLES.has(r));
}

/**
 * Zakládání bodových objektů na mapě (SPOJKOVISTE / JAMA / INFO + admin-only typy pro import).
 * Terénní role (stavba, montáž, záfuk, optika), admin/superadmin a role `import`.
 */
export function canCreateMapObjects(roles: string[] | undefined | null): boolean {
  const normalized = normalizeRoles(roles);
  if (!normalized.length) return false;
  if (normalized.some((r) => r === ROLES.ADMIN || r === ROLES.SUPERADMIN || r === ROLES.IMPORT)) {
    return true;
  }
  return normalized.some(
    (r) => FIELD_ROLES_STAVBA_MONTAZ_ZAFUK_OPTIKA.has(r) || r === 'montáž' || r === 'záfuk',
  );
}

/**
 * Podepsat / uzavřít akvizici (Souhlas, podpis, smlouva, Status ACQ).
 * Mutátoři obsahu objektu **plus** role `akvizice`. Čistý viewer / dokumentarista / úřady = ne.
 * Výkres Akvizice vidí všechny role (`userCanViewAcquisitionDrawing`);
 * v pickeru vždy po field výkresech a před Obecným (`getPickerDrawingsForUser`).
 */
export function canSignAcquisition(roles: string[] | undefined | null): boolean {
  return hasObjectContentMutatorRole(roles) || hasAkviziceRole(roles);
}

/**
 * Generace / podpis / parties / přílohy / archivace akvizičních smluv —
 * stejná množina jako {@link canSignAcquisition} (ne celý modul importů / financí).
 */
export function canGenerateAcquisitionContracts(roles: string[] | undefined | null): boolean {
  return canSignAcquisition(roles);
}

/**
 * Role bez Materials hub / osobních materiálových převodů (desk / dohled / skladové role).
 * Osobní AutoCRM sklad (`users.warehouse_id`) dostává **každý** uživatel — viz
 * {@link isEligibleForWorkerWarehouse}. Tento seznam jen omezuje Materials hub UI.
 *
 * @deprecated Název zachován kvůli starším testům; používá se pro {@link hasWarehouseAccessRole}.
 */
export const WORKER_WAREHOUSE_EXCLUDED_ROLES: readonly RoleId[] = [
  ROLES.VIEWER,
  ROLES.AKVIZICE,
  ROLES.DOKUMENTARISTA,
  ROLES.PRIPRAVA_STAVEB,
  ROLES.BREMENA,
  ROLES.DATOVKA,
  ROLES.PERSONALISTKA,
  ROLES.NAVOLAVANI,
  ROLES.FINANCE,
  ROLES.IMPORT,
  ROLES.SKLAD,
  ROLES.ZARIZENI,
  ROLES.ORG_ADMIN,
] as const;

const MATERIALS_HUB_EXCLUDED = new Set<string>(WORKER_WAREHOUSE_EXCLUDED_ROLES);

/**
 * Role s Materials hub (osobní materiál / osobní↔osobní převody).
 * Nezávislé na tom, že osobní sklad mají všichni.
 */
export const WORKER_WAREHOUSE_ELIGIBLE_ROLES: readonly RoleId[] = ASSIGNABLE_ROLES.filter(
  (r) => !MATERIALS_HUB_EXCLUDED.has(r),
);

const MATERIALS_HUB_ELIGIBLE = new Set<string>(WORKER_WAREHOUSE_ELIGIBLE_ROLES);

/**
 * Nárok na osobní AutoCRM sklad Pracovníci — **vždy ano** (nezávisle na roli).
 * Provisioning: registrace / login / `ensure-worker` / warehouse sync.
 */
export function isEligibleForWorkerWarehouse(_roles?: string[] | undefined | null): boolean {
  return true;
}

/**
 * Osobní materiál / Materials hub / osobní↔osobní převody —
 * terénní / admin / editor (ne desk-only). Samotný finance / sklad používá
 * {@link hasWarehouseIssueRole} pro Výdej; API gate = {@link hasWarehouseApiAccessRole}.
 * Osobní sklad mají i role mimo hub ({@link isEligibleForWorkerWarehouse}).
 */
export function hasWarehouseAccessRole(roles: string[] | undefined | null): boolean {
  return normalizeRoles(roles).some((r) => MATERIALS_HUB_ELIGIBLE.has(r));
}

/**
 * HTTP přístup ke skladovým endpointům (čtení zásob, převody, výdejky, sken) —
 * osobní materiál **nebo** Výdej (role `sklad`).
 */
export function hasWarehouseApiAccessRole(roles: string[] | undefined | null): boolean {
  return hasWarehouseAccessRole(roles) || hasWarehouseIssueRole(roles) || hasOrgAdminRole(roles);
}

/**
 * Nákup / dodavatelské ceníky — čtení a quote (košík).
 * Finance + sklad/admin + role s osobním skladem (širší než samotný Materials hub).
 */
export function hasMaterialProcurementAccessRole(roles: string[] | undefined | null): boolean {
  const normalized = normalizeRoles(roles);
  if (!normalized.length) return false;
  if (hasFinanceAdminOrSuperAdminRole(normalized)) return true;
  if (hasWarehouseIssueRole(normalized)) return true;
  return hasWarehouseAccessRole(normalized);
}

/**
 * Mutace dodavatelů a ceníkových cen — finance / admin / superadmin / sklad.
 */
export function hasMaterialProcurementEditRole(roles: string[] | undefined | null): boolean {
  const normalized = normalizeRoles(roles);
  if (!normalized.length) return false;
  if (hasFinanceAdminOrSuperAdminRole(normalized)) return true;
  return normalized.some((x) => x === ROLES.SKLAD);
}

/**
 * Název osobního skladu v AutoCRM: „Příjmení Jméno“ (stejný řád jako live sklady).
 */
export function formatWorkerWarehouseName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  const last = String(lastName ?? '').trim();
  const first = String(firstName ?? '').trim();
  return [last, first].filter(Boolean).join(' ').trim();
}

function normalizeEmailForAcl(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Osobní HR Drive složka uživatele (`02_Smlouvy_uzivatelu/…`) a majetkové karty:
 * pouze samotný uživatel, personalistka nebo superadmin (ne běžný admin).
 */
export function canAccessUserPersonalDriveFolder(
  viewerRoles: string[] | undefined | null,
  viewerEmail: string | null | undefined,
  targetUserEmail: string | null | undefined,
): boolean {
  const target = normalizeEmailForAcl(targetUserEmail);
  const viewer = normalizeEmailForAcl(viewerEmail);
  if (target && viewer && target === viewer) return true;
  if (hasPersonalistkaRole(viewerRoles)) return true;
  if (hasSuperAdminRole(viewerRoles)) return true;
  return false;
}

/**
 * Polygon Dokumenty: nahrání / vytvoření (vč. Předání povrchů).
 * Všichni kromě čistého vieweru — stejná množina jako mutace obsahu objektu.
 */
export function canCreatePolygonDocuments(roles: string[] | undefined | null): boolean {
  return hasObjectContentMutatorRole(roles);
}

/**
 * Spolupráce (propis do výkazu) — jen role, které výkaz používají.
 * Editor/viewer bez terénu/finance ne.
 */
export function canUseWorkShare(roles: string[] | undefined | null): boolean {
  return hasFieldWorkRole(roles) || hasFinanceAdminOrSuperAdminRole(roles);
}
